import { useCampaignCode } from '@campaign-services/hooks/useCampaignCode'
import { Platform } from '@cart-services/hooks/fp-b2b/store-map'
import { useFpB2bCartService } from '@cart-services/hooks/fp-b2b/use-fp-b2b-cart-service'
import { useFpB2bCoupons } from '@cart-services/hooks/fp-b2b/use-fp-b2b-coupons'
import { useFpB2bPaymentService } from '@cart-services/hooks/fp-b2b/use-fp-b2b-payment-service'
import { useFpB2bProductCatalogProducts } from '@cart-services/hooks/fp-b2b/use-fp-b2b-product-catalog-products'
import { BUTLER_JWT_TOKEN_KEY, COSIGN_TOKEN_KEY, COSIGN_X_PLATFORM_TOKEN, type CountryCode, type ProjectCode } from '@constants'
import { datadogRum } from '@datadog/browser-rum'
import { useApiCampaignActivityInfo } from '@hooks-api/fortune/useApiCampaignActivityInfo'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import * as stylex from '@stylexjs/stylex'
import Cookies from 'js-cookie'
import { Redirect, Route, Switch, useHistory } from 'react-router-dom-v5'
import { getClient } from '../../../clients'
import { AppBarProvider } from '../../../components/AppBar/AppBarProvider'
import { DialogPartnerAuth } from '../../../components/DialogPartnerAuth/DialogPartnerAuth'
import { LoaderBuygather } from '../../../components/LoaderFullPage/LoaderBuygather'
import { TheMerchantHeader } from '../../../components/TheMerchantHeader'
import { AiPurchaseOrder } from '../../fp-b2b-shared/ai-purchase-order/AiPurchaseOrder'
import { AiPurchaseOrderRecommendations } from '../../fp-b2b-shared/ai-purchase-order/AiPurchaseOrderRecommendations'
import { RefreshButlerToken } from '../../fp-b2b-shared/ai-purchase-order/components/RefreshButlerToken/RefreshButlerToken'
import { CheckoutProvider } from '../../fp-b2b-shared/components/CheckoutProvider/CheckoutProvider'
import { FpB2bProductCatalogProvider } from '../../fp-b2b-shared/components/ProductCatalogProvider/ProductCatalogProvider'
import { ThankyouProvider } from '../../fp-b2b-shared/components/ThankyouProvider/ThankyouProvider'
import { Logout } from '../../Logout'
import { MerchantButlerProvider } from '../ai-purchase-order/MerchantButlerProvider'
import { Cart } from '../cart/Cart'
import { CartProvider } from '../cart/components/CartProvider/CartProvider'
import { MerchantCheckoutProcessor } from '../checkout/MerchantCheckout'
import { MerchantRetryPaymentProcessor } from '../checkout/MerchantRetryPayment'
import { merchantCustomerServiceLineMap } from '../MerchantLogin/linkMaps'
import { RefreshCosignToken } from '../MerchantLogin/RefreshCosignToken'
import { MerchantOrderList } from '../MerchantOrder/MerchantOrderList'
import { OrderDetailContainer } from '../OrderDetailContainer'
import { ProductCatalog } from '../product-catalog/ProductCatalog'
import { MerchantThankyou } from '../thankyou/MerchantThankyou'

const styles = stylex.create({
  productCatalogHeader: {
    boxShadow: 'none',
    borderBottomColor: colors.grey200,
    borderBottomStyle: 'solid',
    borderBottomWidth: 1,
  },
})

/**
 * useFpB2bCoupons prefetch all available coupons
 * useFpB2bProductCatalogProducts prefetch all products
 */
const ConditionalLoadData = (props: { campaignCode?: string, campaignId: number, projectCode: ProjectCode, countryCode: CountryCode }) => {
  const { campaignCode, campaignId, projectCode, countryCode } = props
  useFpB2bCoupons(campaignId, projectCode, countryCode, campaignCode, Platform.MERCHANT)
  useFpB2bProductCatalogProducts(campaignId, projectCode, campaignCode, Platform.MERCHANT)
  return null
}

export const AuthRoutes = () => {
  const { campaignCodeFromUrl } = useCampaignCode()
  const history = useHistory()
  const { destructuredData: campaign } = useApiCampaignActivityInfo(campaignCodeFromUrl)
  const projectCode = campaign?.brand_codes_info[0]?.project_code
  const campaignId = campaign?.campaign_id
  const { region: countryCode, language } = campaign || {}
  const { resetCartValues, isCartEmpty } = useFpB2bCartService(campaignId ?? 0, Platform.MERCHANT)
  const { resetPaymentTransaction } = useFpB2bPaymentService({
    campaignId: campaignId ?? 0,
    paymentTransactionIdFromUrl: undefined,
    campaignCode: campaignCodeFromUrl,
    platform: Platform.MERCHANT,
  })

  const handleLogout = () => {
    Cookies.remove(`${campaignCodeFromUrl}_${COSIGN_TOKEN_KEY}`)
    Cookies.remove(`${campaignCodeFromUrl}_${COSIGN_X_PLATFORM_TOKEN}`)
    Cookies.remove(BUTLER_JWT_TOKEN_KEY)
    resetCartValues()
    resetPaymentTransaction()
    getClient('fortune').defaults.headers['access-token'] = ''
    datadogRum.clearUser()
    window.location.href = `/merchant/${campaignCodeFromUrl}/login` // need to refresh the page to clear the cache
  }

  const handleToCart = () => {
    history.push(`/merchant/${campaignCodeFromUrl}/cart`)
  }

  if (!projectCode || !countryCode || !campaignId) {
    return <LoaderBuygather fontSize={60} />
  }

  return (
    <>
      <RefreshCosignToken />
      {projectCode && countryCode && campaignId
      && <ConditionalLoadData campaignCode={campaignCodeFromUrl} projectCode={projectCode} countryCode={countryCode} campaignId={campaignId} />}
      <DialogPartnerAuth
        onLogout={handleLogout}
        title={<Text variant="h4">登入異常</Text>}
        content={<Text variant="body">{`請聯繫負責業務或官方 LINE ${merchantCustomerServiceLineMap[projectCode]}`}</Text>}
      />
      <AppBarProvider onLogout={handleLogout} isCartDisabled={isCartEmpty} toCartPage={handleToCart} environment="merchant">
        <CartProvider
          projectCode={projectCode}
          countryCode={countryCode}
          campaignId={campaignId}
          campaignCodeFromUrl={campaignCodeFromUrl}
          currency={campaign?.currency}
        >
          <Switch>
            <Route path="/merchant/:code/product-catalog" exact>
              {projectCode && countryCode && campaignId
              && (
                <>
                  <RefreshButlerToken projectCode={projectCode} campaignCode={campaignCodeFromUrl} />
                  <FpB2bProductCatalogProvider
                    platform={Platform.MERCHANT}
                    projectCode={projectCode}
                    countryCode={countryCode}
                    showButler
                    currency={campaign?.currency}
                    campaignId={campaignId}
                    campaignCode={campaignCodeFromUrl}
                  >
                    <TheMerchantHeader rootStyle={styles.productCatalogHeader} projectCode={projectCode} countryCode={countryCode} campaignId={campaignId} />
                    <ProductCatalog />
                  </FpB2bProductCatalogProvider>
                </>
              )}
            </Route>
            <Route path="/merchant/:code/ai-purchase-order/recommendations">
              {projectCode && countryCode && campaignId
              && (
                <MerchantButlerProvider projectCode={projectCode} countryCode={countryCode} campaignId={campaignId} campaignCode={campaignCodeFromUrl}>
                  <TheMerchantHeader projectCode={projectCode} countryCode={countryCode} campaignId={campaignId} />
                  <AiPurchaseOrderRecommendations />
                </MerchantButlerProvider>
              )}
            </Route>
            <Route path="/merchant/:code/ai-purchase-order">
              {projectCode && countryCode && campaignId
              && (
                <MerchantButlerProvider projectCode={projectCode} countryCode={countryCode} campaignId={campaignId} campaignCode={campaignCodeFromUrl}>
                  <TheMerchantHeader projectCode={projectCode} countryCode={countryCode} campaignId={campaignId} />
                  <AiPurchaseOrder />
                </MerchantButlerProvider>
              )}
            </Route>
            <Route path="/merchant/:code/cart">
              {projectCode && countryCode && campaignId
              && (
                <>
                  <RefreshButlerToken projectCode={projectCode} campaignCode={campaignCodeFromUrl} />
                  <TheMerchantHeader projectCode={projectCode} countryCode={countryCode} campaignId={campaignId} />
                  <Cart />
                </>
              )}
            </Route>
            <Route path="/merchant/:code/checkout">
              {
                projectCode && countryCode && language && campaignId
                && (
                  <CheckoutProvider
                    platform={Platform.MERCHANT}
                    projectCode={projectCode}
                    currency={campaign?.currency}
                    countryCode={countryCode}
                    language={language}
                    campaignId={campaignId}
                    campaignCode={campaignCodeFromUrl}
                    env={import.meta.env.VITE_APP_ENV === 'production' ? 'production' : 'sandbox'}
                    fallbackUrl={`/merchant/${campaignCodeFromUrl}/product-catalog`}
                  >
                    <TheMerchantHeader projectCode={projectCode} countryCode={countryCode} campaignId={campaignId} />
                    <MerchantCheckoutProcessor />
                  </CheckoutProvider>
                )
              }
            </Route>
            <Route path="/merchant/:code/retry-payment/:transactionId">
              {
                projectCode && countryCode && language && campaignId
                && (
                  <CheckoutProvider
                    platform={Platform.MERCHANT}
                    projectCode={projectCode}
                    currency={campaign?.currency}
                    countryCode={countryCode}
                    language={language}
                    campaignId={campaignId}
                    campaignCode={campaignCodeFromUrl}
                    env={import.meta.env.VITE_APP_ENV === 'production' ? 'production' : 'sandbox'}
                  >
                    <TheMerchantHeader projectCode={projectCode} countryCode={countryCode} campaignId={campaignId} />
                    <MerchantRetryPaymentProcessor />
                  </CheckoutProvider>
                )
              }
            </Route>
            <Route path="/merchant/:code/thankyou/:transactionId">
              {
                projectCode && countryCode && campaignId
                && (
                  <ThankyouProvider
                    projectCode={projectCode}
                    countryCode={countryCode}
                    currency={campaign?.currency}
                    campaignId={campaignId}
                    platform={Platform.MERCHANT}
                  >
                    <TheMerchantHeader projectCode={projectCode} countryCode={countryCode} campaignId={campaignId} />
                    <MerchantThankyou />
                  </ThankyouProvider>
                )
              }
            </Route>
            <Route path="/merchant/:code/my-account/order">
              {
                projectCode && countryCode && campaignId
                && <MerchantOrderList projectCode={projectCode} countryCode={countryCode} campaignId={campaignId} siteId={campaign.site_id} />
              }
            </Route>
            <Route path="/merchant/:code/my-account/order-detail/:transactionId">
              {
                projectCode && countryCode && campaignId
                && <OrderDetailContainer projectCode={projectCode} countryCode={countryCode} campaignId={campaignId} environment="merchant" />
              }
            </Route>
            <Route path="/merchant/:code/logout">
              {campaignId && <Logout environment="merchant" campaignId={campaignId} />}
            </Route>
            <Route path="*">
              <Redirect to={`/merchant/${campaignCodeFromUrl}/product-catalog`} />
            </Route>
          </Switch>
        </CartProvider>
      </AppBarProvider>
    </>
  )
}
